import { DataDai } from '../dai/types';
import { OrderType } from '../schemas';

export type UnitDataListDataViewMode = 'unitData' | 'ranking';

export const UNIT_DATA_LIST_DATA_VIEW_MODE_LABELS: Record<
  UnitDataListDataViewMode,
  string
> = {
  unitData: '標準',
  ranking: '出玉ランキング',
};

export const UNIT_DATA_LIST_DATA_VIEW_MODE_TOOLTIPS: Record<
  UnitDataListDataViewMode,
  string
> = {
  unitData: '店舗・台ごとの台別データ一覧を確認できます',
  ranking: '出玉ランキングの集計結果を確認できます',
};

/**
 * 出玉ランキング表示時は比較期間スライダをマスクする
 */
export const shouldMaskSliderComparison = (
  dataViewMode: UnitDataListDataViewMode
): boolean => dataViewMode === 'ranking';

/**
 * 出玉ランキング(dtmaRanking)のデフォルトソート
 */
export const DTMA_RANKING_DEFAULT_SORT = 'diff';
export const DTMA_RANKING_DEFAULT_ORDER: OrderType = 'asc';

/**
 * 出玉ランキングで先頭固定表示する列（この順序で固定表示する）。
 * 標準表示（機種名・台番号の2列）と異なり、日付・機種名・台番号の3列を固定列として扱う。
 */
export const DTMA_RANKING_STICKY_COLUMN_CODES = [
  'dataDay',
  'kiTushoMei',
  'daiBan',
];

/**
 * 標準の検索パラメータを出玉ランキング用に変換する。
 * - 表示項目（fields）は標準と同様に扱う（未指定ならサーバー既定、選択があればその項目を送る）。
 *   標準⇔出玉ランキングの表示項目セットは別なので、切替時の項目リセット/復元は
 *   reducer 側（CHANGE_UNIT_DATA_LIST_REPORTS_DATA_VIEW_MODE）で行う。
 * - ソート（sort/order）は標準と同様に BE へ送り、サーバーソートに委ねる。
 *   ただし sort 未指定（リロード時の初期化タイミングや標準からの残り）の場合は、
 *   標準の検索条件（sort/order。dataDaiDefaultValue の order='asc' 等）がそのまま送られて
 *   意図しない並びで表示されてしまうのを防ぐため、ランキング既定（差の昇順）を
 *   補って送る。sort の有無だけで判定することで初期化タイミングに依存せず既定を保証する。
 * - 出玉ランキングに比較期間は無いため比較期間パラメータを除去する
 */
export const buildDtmaRankingFetchParams = (
  params: DataDai['setting']
): DataDai['setting'] => {
  const next: DataDai['setting'] = { ...params };
  // sort/order は対で決まる（ヘッダー操作・reducerシード・お気に入り）ため、sort が未指定なら
  // order の残り（標準の既定 asc 等）に引きずられないよう、両方をランキング既定で補完する。
  if (next.sort == null || next.sort === '') {
    next.sort = DTMA_RANKING_DEFAULT_SORT;
    next.order = DTMA_RANKING_DEFAULT_ORDER;
  }
  // 出玉ランキングに比較期間は無いため排除
  delete next.ymdComparisonList;
  delete next.startComparisonDate;
  delete next.endComparisonDate;
  return next;
};

/**
 * 台別データ一覧（標準表示）のパス
 */
export const UNIT_DATA_LIST_BASE_PATH = '/report/dai';

/**
 * 台別データ一覧（出玉ランキング表示）のパス
 * チェーン店レポート（/report/chain/ki 等）と同様に表示区分をURLで表す
 */
export const UNIT_DATA_LIST_RANKING_PATH = '/report/dai/ranking';

/**
 * pathname から表示区分を導出する
 * （Amplify 環境で付与される末尾スラッシュを除去して判定する）
 */
export const pathnameToDataViewMode = (
  pathname: string
): UnitDataListDataViewMode =>
  pathname.replace(/\/$/, '') === UNIT_DATA_LIST_RANKING_PATH
    ? 'ranking'
    : 'unitData';

/**
 * 表示区分から遷移先のパスを導出する
 */
export const dataViewModeToPath = (mode: UnitDataListDataViewMode): string =>
  mode === 'ranking' ? UNIT_DATA_LIST_RANKING_PATH : UNIT_DATA_LIST_BASE_PATH;
